import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { FolderKanban, Plus, Loader2, Music2, Trash2, ChevronRight, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import api from "@/lib/api";
import { toast } from "sonner";

export default function Projects() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "" });
  const [creating, setCreating] = useState(false);

  const fetchProjects = async () => {
    try {
      const { data } = await api.get("/projects");
      setProjects(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchProjects(); }, []);

  const create = async () => {
    if (!form.name.trim()) { toast.error("Project name is required."); return; }
    setCreating(true);
    try {
      await api.post("/projects", form);
      setForm({ name: "", description: "" });
      setOpen(false);
      await fetchProjects();
      toast.success("Project created.");
    } catch {
      toast.error("Could not create project.");
    } finally {
      setCreating(false);
    }
  };

  const remove = async (id, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/projects/${id}`);
      await fetchProjects();
      toast.success("Project deleted (tracks kept in Library).");
    } catch {
      toast.error("Could not delete project.");
    }
  };

  return (
    <div className="p-6 lg:p-8 max-w-6xl">
      <header className="mb-8 flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-4xl font-extrabold flex items-center gap-3">
            <FolderKanban className="text-primary" /> Projects
          </h1>
          <p className="text-muted-foreground mt-1">Group soundtracks by game — each title keeps its own library.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-primary text-primary-foreground hover:bg-primary/90 glow-primary" data-testid="new-project-button">
              <Plus size={16} className="mr-1.5" /> New Project
            </Button>
          </DialogTrigger>
          <DialogContent className="glass border-border">
            <DialogHeader><DialogTitle className="font-display">Create a project</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label>Game / Project name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Neon Drift" className="bg-background border-border" data-testid="project-name-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Description</Label>
                <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Cyberpunk racing game soundtrack" className="bg-background border-border resize-none" data-testid="project-desc-input" />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={create} disabled={creating} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="create-project-submit">
                {creating ? <Loader2 className="animate-spin" size={16} /> : "Create Project"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </header>

      {loading ? (
        <div className="text-muted-foreground font-mono animate-pulse">Loading…</div>
      ) : projects.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-12 text-center text-muted-foreground" data-testid="empty-projects">
          No projects yet. Create one to organize your soundtracks.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {projects.map((p, i) => (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.04 }}
              onClick={() => navigate(`/dashboard/projects/${p.id}`)}
              className="group rounded-2xl border border-border bg-card p-6 cursor-pointer hover:border-primary/40 transition-colors"
              data-testid={`project-card-${p.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="w-11 h-11 rounded-xl bg-primary/10 border border-primary/30 flex items-center justify-center">
                  <FolderKanban className="text-primary" size={20} />
                </div>
                {p.role === "owner" ? (
                  <Button variant="ghost" size="icon" onClick={(e) => remove(p.id, e)} className="text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`delete-project-${p.id}`}>
                    <Trash2 size={16} />
                  </Button>
                ) : (
                  <Badge className="bg-accent/20 text-accent border-accent/30" data-testid={`shared-badge-${p.id}`}>Shared with you</Badge>
                )}
              </div>
              <h3 className="font-display text-xl font-bold mt-4">{p.name}</h3>
              {p.description && <p className="text-sm text-muted-foreground line-clamp-2 mt-1">{p.description}</p>}
              <div className="flex items-center justify-between mt-5 pt-4 border-t border-border">
                <span className="flex items-center gap-3 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1.5"><Music2 size={14} className="text-primary" /> {p.generation_count} tracks</span>
                  {p.member_count > 0 && <span className="flex items-center gap-1.5"><Users size={14} className="text-secondary" /> {p.member_count + 1}</span>}
                </span>
                <ChevronRight size={18} className="text-muted-foreground group-hover:text-primary transition-colors" />
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
